import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  CheckCircle,
  ArrowRight,
  Search,
  FileCheck,
  Shield,
  Clock,
  Award,
  Users,
  Database,
} from "lucide-react";
import TestimonialsSlider from "../components/TestimonialsSlider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const TrademarkSearchNew = () => {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: "Federal & State Search",
      price: 149,
      features: [
        "Federal trademark search (USPTO database)",
        "All 50 states trademark search",
        "Detailed online report",
        "Conflict identification",
        "Risk assessment",
        "Email support",
      ],
    },
    {
      name: "Comprehensive Search",
      price: 299,
      popular: true,
      features: [
        "Everything in Federal & State Search",
        "Common law trademark search",
        "Corporate name search (all 50 states)",
        "Corporate directories search",
        "Internet & domain name search",
        "Detailed analysis report",
        "Expert recommendations",
        "Priority support",
      ],
    },
    {
      name: "Global Search",
      price: 499,
      features: [
        "Everything in Comprehensive Search",
        "European Community trademark search",
        "WIPO international trademark search",
        "Major international jurisdictions",
        "Multi-national conflict analysis",
        "Global protection strategy",
        "Expert consultation (30 min)",
        "24/7 priority support",
      ],
    },
  ];

  const faqs = [
    {
      q: "Why do I need a trademark search?",
      a: "A trademark search helps identify potential conflicts before you file, saving you time and money. It reveals existing trademarks that might prevent your registration or lead to legal disputes. Our comprehensive search increases your chances of successful registration.",
    },
    {
      q: "What does a trademark search include?",
      a: "Our searches include registered trademarks, pending applications, state registrations, common law uses, domain names, and business entity names. The scope depends on your package - from basic federal searches to comprehensive global searches.",
    },
    {
      q: "How long does a trademark search take?",
      a: "Most searches are completed within 2-3 business days. Comprehensive and Global searches may take 3-5 business days due to the extensive research involved.",
    },
    {
      q: "What if conflicts are found?",
      a: "If we find potential conflicts, our detailed report will outline the risks and provide recommendations. We can help you modify your trademark or develop a strategy to address the conflicts before filing.",
    },
    {
      q: "Is a search required for trademark registration?",
      a: "While not legally required, a professional search is strongly recommended. The USPTO will conduct its own search, but identifying conflicts beforehand helps avoid rejection and potential legal issues.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* About the Service */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Comprehensive Trademark Search Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand investment with a thorough trademark search.
              Identify potential conflicts before filing and increase your
              chances of successful registration with our professional search
              services starting at just $149.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/")}
              className="bg-brand-gradient text-white hover:opacity-90 group animate-pulse-glow"
            >
              Start Your Search Today
              <ArrowRight
                className="ml-2 group-hover:translate-x-1 transition-transform"
                size={20}
              />
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
            Our Simple{" "}
            <span className="text-brand-primary">3-Step Process</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: FileCheck,
                title: "Submit Your Request",
                desc: "Provide your trademark details through our simple online form. Tell us about your mark.",
              },
              {
                icon: Search,
                title: "We Conduct Research",
                desc: "Our experts perform comprehensive searches across multiple databases to identify conflicts.",
              },
              {
                icon: Shield,
                title: "Receive Detailed Report",
                desc: "Get a detailed analysis report with findings, risk assessment, and expert recommendations.",
              },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={idx}
                  className="text-center animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-brand-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
            Why You Need a{" "}
            <span className="text-brand-primary">Trademark Search</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Avoid Rejection",
                desc: "Identify conflicting marks before filing to avoid costly USPTO rejections and wasted fees.",
              },
              {
                title: "Save Time & Money",
                desc: "Prevent expensive rebranding and legal disputes by finding conflicts early in the process.",
              },
              {
                title: "Legal Protection",
                desc: "Ensure you're not infringing on existing trademarks and avoid potential lawsuits.",
              },
              {
                title: "Strategic Advantage",
                desc: "Make informed decisions about your trademark strategy with comprehensive market intelligence.",
              },
              {
                title: "Clear Path Forward",
                desc: "Get expert recommendations on how to proceed with confidence in your trademark application.",
              },
              {
                title: "Peace of Mind",
                desc: "File your trademark application knowing you've done thorough due diligence.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all animate-scale-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Choose Your Search Package
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From basic federal searches to comprehensive global analysis
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan, idx) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full animate-scale-in ${plan.popular ? "bg-gradient-to-br from-emerald-600 to-teal-500 text-white shadow-2xl ring-4 ring-emerald-200" : "bg-white shadow-lg hover:shadow-xl border border-emerald-100"}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="text-center mb-6">
                  <h3
                    className={`text-2xl font-bold mb-2 ${plan.popular ? "text-white" : "text-gray-800"}`}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center">
                    <span
                      className={`text-5xl font-bold ${plan.popular ? "text-white" : "text-brand-primary"}`}
                    >
                      ${plan.price}
                    </span>
                  </div>
                  <p
                    className={`text-sm mt-2 ${plan.popular ? "text-emerald-50" : "text-gray-500"}`}
                  >
                    One-time fee
                  </p>
                </div>
                <div className="flex-grow mb-6 overflow-y-auto max-h-64 pr-2 scrollbar-thin">
                  <div className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <CheckCircle
                          className={`flex-shrink-0 mt-0.5 ${plan.popular ? "text-emerald-100" : "text-brand-primary"}`}
                          size={20}
                        />
                        <span
                          className={`text-sm ${plan.popular ? "text-white" : "text-gray-600"}`}
                        >
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() => navigate("/")}
                  className={`w-full mt-auto ${plan.popular ? "bg-white text-brand-primary hover:bg-gray-50 border-2 border-brand-primary" : "bg-brand-gradient text-white hover:opacity-90"}`}
                >
                  Choose Plan
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16 animate-fade-in">
            Why Choose{" "}
            <span className="text-brand-primary">Trademark Law Associates</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Database,
                title: "Comprehensive Research",
                desc: "Extensive searches across federal, state, and international databases",
              },
              {
                icon: Clock,
                title: "Fast Turnaround",
                desc: "Most searches completed within 2-3 business days",
              },
              {
                icon: Award,
                title: "Expert Analysis",
                desc: "Detailed reports with professional risk assessment and recommendations",
              },
              {
                icon: Users,
                title: "Ongoing Support",
                desc: "Expert guidance on how to proceed based on search results",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="text-center animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-base text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img
                src="https://images.unsplash.com/photo-1551135049-8a33b5883817?w=800"
                alt="Trademark Search Benefits"
                className="rounded-2xl shadow-xl hover-zoom"
              />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                What's Included in Our Search
              </h2>
              <div className="space-y-4">
                {[
                  "USPTO federal trademark database search",
                  "All 50 states trademark registrations",
                  "Common law trademark research",
                  "Domain name availability check",
                  "Corporate name database search",
                  "Detailed risk assessment and recommendations",
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle
                      className="text-brand-primary flex-shrink-0 mt-1"
                      size={24}
                    />
                    <p className="text-lg text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <TestimonialsSlider /> */}

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
              Frequently Asked{" "}
              <span className="text-brand-primary">Questions</span>
            </h2>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-800">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-gray-600">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Search Your Trademark?
            </h2>
            <p className="text-xl text-emerald-50 mb-8">
              Get comprehensive trademark search results and protect your brand
              investment
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/")}
              className="bg-white text-brand-primary hover:bg-gray-50 shadow-xl border-2 border-white"
            >
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrademarkSearchNew;
